/**
 * validateAnalysisShape — Bouclier anti-crash pour la réponse Vision AI.
 *
 * Garantit que les composants de rendu reçoivent TOUJOURS une structure
 * complète, même si l'IA renvoie un JSON partiel ou malformé.
 *
 * Champs critiques garantis (avec valeur par défaut 0 / [] / null) :
 *   - analysis.total_calories (number, default 0)
 *   - analysis.total_protein_g, total_carbs_g, total_fats_g, total_fiber_g
 *   - analysis.foods_detected (array, default [])
 *   - analysis.processed_foods_detected (array, default [])
 *   - analysis.protein_status (string, default 'on_track')
 *   - analysis.meal_points (number, default 0)
 *   - analysis.chef_touch_suggestion, vegetable_suggestion, ai_feedback (string|null)
 *
 * Si la racine est complètement vide ou non-objet, on renvoie un fallback
 * minimal pour que l'UI affiche le composant <meal-analysis-fallback>.
 */
const num = (v, def = 0) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : def;
};

const arr = (v) => (Array.isArray(v) ? v : []);

// Coerce each entry to a plain non-empty string. OpenAI Vision sometimes
// returns objects (e.g. `{name: "saucisse", reason: "..."}`) where the schema
// declares `List[str]` — without this guard, downstream `.toLowerCase()` /
// `.join(', ')` calls crash and the user sees "Analyse difficile".
const arrOfStr = (v) => arr(v)
  .map((item) => {
    if (typeof item === 'string') return item.trim();
    if (item && typeof item === 'object') {
      const label = item.name || item.item || item.label || item.food || '';
      return typeof label === 'string' ? label.trim() : '';
    }
    if (item == null) return '';
    return String(item).trim();
  })
  .filter(Boolean);

const validateFood = (f) => {
  if (!f || typeof f !== 'object') return null;
  return {
    name: typeof f.name === 'string' ? f.name : 'Aliment',
    portion: typeof f.portion === 'string' ? f.portion : '',
    calories: num(f.calories, 0),
    protein_g: num(f.protein_g, 0),
    carbs_g: num(f.carbs_g, 0),
    fats_g: num(f.fats_g, 0),
    fiber_g: num(f.fiber_g, 0),
    is_processed: !!f.is_processed,
    is_super_aliment: !!f.is_super_aliment,
    ...f, // keep additional fields
  };
};

export default function validateAnalysisShape(rawData) {
  // Edge case: backend returned ramadan_block or some non-analysis payload
  if (!rawData || typeof rawData !== 'object') {
    return { analysis: null, _validation_error: 'empty_response' };
  }

  // Pass through ramadan_block / non-analysis flow
  if (rawData.ramadan_block) return rawData;

  const a = rawData.analysis || {};
  if (typeof a !== 'object' || a === null) {
    return { ...rawData, analysis: null, _validation_error: 'analysis_not_object' };
  }

  // Build a sanitized analysis shape with safe defaults everywhere
  const safeAnalysis = {
    total_calories: num(a.total_calories, 0),
    total_protein_g: num(a.total_protein_g, 0),
    total_carbs_g: num(a.total_carbs_g, 0),
    total_fats_g: num(a.total_fats_g, 0),
    total_fiber_g: num(a.total_fiber_g, 0),
    foods_detected: arr(a.foods_detected || a.foods).map(validateFood).filter(Boolean),
    processed_foods_detected: arrOfStr(a.processed_foods_detected),
    protein_status: typeof a.protein_status === 'string' ? a.protein_status : 'on_track',
    meal_points: num(a.meal_points, 0),
    chef_touch_suggestion: typeof a.chef_touch_suggestion === 'string' ? a.chef_touch_suggestion : null,
    vegetable_suggestion: typeof a.vegetable_suggestion === 'string' ? a.vegetable_suggestion : null,
    ai_feedback: typeof a.ai_feedback === 'string' ? a.ai_feedback : null,
    off_track_alert: typeof a.off_track_alert === 'string' ? a.off_track_alert : null,
    clarification_questions: arr(a.clarification_questions),
    // Keep any extra fields the backend sends
    ...a,
  };

  // Ensure normalized fields override raw ones
  safeAnalysis.total_calories = num(a.total_calories, 0);
  safeAnalysis.total_protein_g = num(a.total_protein_g, 0);
  safeAnalysis.total_carbs_g = num(a.total_carbs_g, 0);
  safeAnalysis.total_fats_g = num(a.total_fats_g, 0);
  safeAnalysis.total_fiber_g = num(a.total_fiber_g, 0);
  safeAnalysis.foods_detected = arr(a.foods_detected || a.foods).map(validateFood).filter(Boolean);

  // Critical sanity check — if zero calories AND zero foods, mark invalid
  // (the modal fallback will show a friendly retry message)
  if (safeAnalysis.total_calories === 0 && safeAnalysis.foods_detected.length === 0) {
    return { ...rawData, analysis: null, _validation_error: 'empty_analysis' };
  }

  return { ...rawData, analysis: safeAnalysis };
}
